import { createContext, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

interface AppMetadata {
  appName: string;
  tabTitle: string;
  logoUrl: string;
  metaDescription: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageUrl: string | null;
  robotsNoindexEnabled: boolean;
  themeBgLight: string | null;
  themeCardLight: string | null;
  themeBgDark: string | null;
  themeCardDark: string | null;
}

interface AppBrandingContextType {
  appName: string;
  logoUrl: string;
  isLoading: boolean;
}

const AppBrandingContext = createContext<AppBrandingContextType>({
  appName: "BETI",
  logoUrl: "/api/assets/branding/logo",
  isLoading: true,
});

function updateMetaTag(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.querySelector(`meta[property="${name}"]`) as HTMLMetaElement | null;
  }
  if (!meta) {
    meta = document.createElement("meta");
    if (name.startsWith("og:")) {
      meta.setAttribute("property", name);
    } else {
      meta.setAttribute("name", name);
    }
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function updateMetaRobots(robotsNoindexEnabled: boolean) {
  let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
  
  if (robotsNoindexEnabled) {
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", "noindex,nofollow");
  } else {
    if (meta) {
      meta.remove();
    }
  }
}

function applyThemeColors(data: AppMetadata) {
  const root = document.documentElement;
  const isDark = root.classList.contains("dark");

  if (isDark) {
    if (data.themeBgDark) {
      root.style.setProperty("--background", data.themeBgDark);
    } else {
      root.style.removeProperty("--background");
    }
    if (data.themeCardDark) {
      root.style.setProperty("--card", data.themeCardDark);
    } else {
      root.style.removeProperty("--card");
    }
  } else {
    if (data.themeBgLight) {
      root.style.setProperty("--background", data.themeBgLight);
    } else {
      root.style.removeProperty("--background");
    }
    if (data.themeCardLight) {
      root.style.setProperty("--card", data.themeCardLight);
    } else {
      root.style.removeProperty("--card");
    }
  }
}

export function AppBrandingProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useQuery<AppMetadata>({
    queryKey: ["/api/app-metadata"],
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (data) {
      updateMetaRobots(data.robotsNoindexEnabled);
      
      if (data.metaDescription) {
        updateMetaTag("description", data.metaDescription);
      }
      if (data.ogTitle) {
        updateMetaTag("og:title", data.ogTitle);
      }
      if (data.ogDescription) {
        updateMetaTag("og:description", data.ogDescription);
      }
      if (data.ogImageUrl) {
        updateMetaTag("og:image", data.ogImageUrl);
      }

      applyThemeColors(data);
    }
  }, [data]);

  useEffect(() => {
    if (!data) return;

    const observer = new MutationObserver(() => {
      applyThemeColors(data);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [data]);

  return (
    <AppBrandingContext.Provider value={{
      appName: data?.appName || "BETI",
      logoUrl: "/api/assets/branding/logo",
      isLoading,
    }}>
      {children}
    </AppBrandingContext.Provider>
  );
}

export const useAppBranding = () => useContext(AppBrandingContext);
